import { ENTRY_HARD_CAP_GROSS, MakerFlag } from '../../types/entry.types';
import { HedgeMode } from '../shared/counterHedgeMode';

// Display-side mirrors of `RateCalculator.netTakerAsk` /
// `calculatePositionTna` for the taker-taker BUY scenario on the opener.
// Kept in sync with `arbitrage-core/services/position-opener/src/constants.ts`.
export const KALSHI_TAKER_FEE_RATE = 0.07;
export const polymarketTakerFeeRate = (category?: string): number => {
  if (category === 'sports') return 0.03;
  if (category === 'crypto') return 0.072;
  return 0.072;
};
export const allInKalshiAsk = (raw: number): number =>
  raw + KALSHI_TAKER_FEE_RATE * raw * (1 - raw);
export const allInPolymarketAsk = (raw: number, category?: string): number => {
  const rate = polymarketTakerFeeRate(category);
  return raw + rate * raw * (1 - raw);
};

// Platform price ticks.
export const POLY_TICK = 0.001;
export const KALSHI_TICK = 0.01;

// ── Threshold (sum-of-asks) bounds — hedge-mode / flag aware ──────────────
// The opener's threshold input ceiling depends on the rule's EFFECTIVE
// counter-hedge mode + its per-rule allow-above-one flag:
//  - `always-threshold` WITHOUT the flag → strictly `< 1.00` (the legacy hard
//    cap: buying both binary sides for ≥ $1 cannot arbitrage). The input `max`
//    stays the step-aligned 0.999 it has always been.
//  - `always-threshold` / `gtc-to-max-sum` WITH the flag → up to `1.4`
//    inclusive. Both share the engine's above-$1 opt-in cap (1.4): the flag
//    now lifts the `gtc-to-max-sum` engine fortress from `market_hedge_max_sum`
//    to 1.4 (2026-07-03), so the FE ceiling must match or the operator could
//    type a value the engine then refuses.
//  - `market-to-hedge` WITH the flag → up to `2.0` inclusive. Its engine cap is
//    uncapped (∞), so only the physical fat-finger ceiling applies.
export const ENTRY_THRESHOLD_CAP_ABOVE_ONE = 1.4;
export const ENTRY_THRESHOLD_CAP_MODE_HEDGE = 2.0;

export interface ThresholdBound {
  /** Numeric ceiling. Whether the ceiling itself is allowed is `inclusive`. */
  max: number;
  /** `true` → value may equal `max` (`<=`); `false` → must be below it (`<`). */
  inclusive: boolean;
  /** `true` when this row may rest above $1 (opted-in or mode-driven). */
  aboveOneAllowed: boolean;
}

/** The threshold ceiling for a row's effective hedge mode + allow flag. */
export function thresholdBound(
  effectiveMode: HedgeMode,
  allowThresholdAboveOne: boolean
): ThresholdBound {
  // The explicit per-pair opt-in is a UNIVERSAL gate for crossing $1, separate
  // from the hedge mode: without it NO mode may exceed $1. With it on, the
  // per-mode value ceiling applies — the hedge mode's own cap is unaffected.
  if (!allowThresholdAboveOne) {
    return { max: 1, inclusive: false, aboveOneAllowed: false };
  }
  // market-to-hedge's engine cap is uncapped (∞), so only the physical $2
  // fat-finger ceiling applies. Every other mode (always-threshold AND
  // gtc-to-max-sum) shares the engine's 1.4 above-$1 opt-in cap.
  if (effectiveMode === 'market-to-hedge') {
    return {
      max: ENTRY_THRESHOLD_CAP_MODE_HEDGE,
      inclusive: true,
      aboveOneAllowed: true
    };
  }
  return {
    max: ENTRY_THRESHOLD_CAP_ABOVE_ONE,
    inclusive: true,
    aboveOneAllowed: true
  };
}

/** `true` when `value` is a valid threshold under `bound` (0 < value [<|<=] max). */
export function isThresholdWithinBound(
  value: number,
  bound: ThresholdBound
): boolean {
  if (!Number.isFinite(value) || value <= 0) return false;
  return bound.inclusive ? value <= bound.max : value < bound.max;
}

/**
 * The numeric `max` attribute for the threshold <input>. For the exclusive
 * (< 1.00) ceiling we keep the legacy step-aligned `ENTRY_HARD_CAP_GROSS`
 * (0.999) so the spinner stops just below $1 exactly as before; inclusive
 * ceilings expose their real max.
 */
export function thresholdInputMax(bound: ThresholdBound): number {
  return bound.inclusive ? bound.max : ENTRY_HARD_CAP_GROSS;
}

/**
 * Effective counter-hedge mode = the rule's own mode, else the global default,
 * else the documented `gtc-to-max-sum` default until the global config loads.
 */
export function resolveEffectiveHedgeMode(
  ruleMode: HedgeMode | null | undefined,
  globalDefault: HedgeMode | undefined
): HedgeMode {
  return ruleMode ?? globalDefault ?? 'gtc-to-max-sum';
}

export const MIN_DAYS = 1 / 24;

export const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}¢`;
};

export const fmtQty = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v <= 0) return '—';
  if (v >= 1_000_000) return `${Math.floor(v / 100_000) / 10}M`;
  if (v >= 1_000) return `${Math.floor(v / 100) / 10}k`;
  if (v >= 100) return String(Math.floor(v));
  return v.toFixed(2);
};

export const fmtPct = (v: number | null): string => {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};

// Continuous-compounding TNA for a path where BOTH legs are bought.
// `sum` is the all-in BUY cost. Returns null when the sum is non-positive
// or the event has fully expired (matches the backend's `MIN_DAYS = 1/24`
// floor + −1 cutoff).
export const takerTnaFromSum = (
  sum: number | null,
  daysToExpiry: number | null | undefined,
): number | null => {
  if (sum == null || sum <= 0 || daysToExpiry == null) return null;
  if (daysToExpiry < -1) return null;
  const days = Math.max(MIN_DAYS, daysToExpiry);
  const profit = 1 / sum - 1;
  return (Math.log(1 + profit) / (days / 365)) * 100;
};

// Each tab governs one of the two entry paths. Tab A = poly YES +
// kalshi NO; Tab B = poly NO + kalshi YES. The poly/kalshi maker switch
// on each tab maps to that path's two maker flags; the TT switch is
// shared (`entryStrategy` is a single rule field) but binds to the
// tab's sides on first activation.
export const TAB_FLAGS: Record<
  'a' | 'b',
  { poly: MakerFlag; kalshi: MakerFlag }
> = {
  a: { poly: 'polyYesMaker', kalshi: 'kalshiNoMaker' },
  b: { poly: 'polyNoMaker', kalshi: 'kalshiYesMaker' },
};

// Side persisted onto a newly-created rule for the given maker flag.
// The backend re-runs the same derivation in RulesService; the values
// here just give it a head start.
export const FLAG_TO_SIDES: Record<
  MakerFlag,
  { polySide: 'yes' | 'no'; kalshiSide: 'yes' | 'no' }
> = {
  polyYesMaker: { polySide: 'yes', kalshiSide: 'no' },
  kalshiNoMaker: { polySide: 'yes', kalshiSide: 'no' },
  polyNoMaker: { polySide: 'no', kalshiSide: 'yes' },
  kalshiYesMaker: { polySide: 'no', kalshiSide: 'yes' },
};
